// src/pages/TrackCreation/Step1.tsx
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Button from "../../../../../components/ui/Button";
import { saveStop, getHotInfoTrackId, getStopById } from "../../../../../api";
import MultiSelectDropdown from "../../../../../components/common/MultiSelectDropdown";
import TitleSubtitle from "../../../../../components/common/TitleSubtitle";
import { useTrackCreation } from "../../../../../context/TrackCreationContext";
import { StopActivitiesProps, RouteParamsWithLanguage, DropdownOption } from "../../../../../types/screensTypes";
import stopEdit from "./StopEdit.module.css";
import stepStyles from "../../createStep.module.css";

const StopActivities: React.FC<StopActivitiesProps> = ({ navigateToNext }) => {
    const { trackData } = useTrackCreation();
    const { wptId, language } = useParams<RouteParamsWithLanguage>(); // Get the project ID and language from the URL
    const [hotInfos, setHotInfos] = useState<DropdownOption[]>([]);
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const [loading, setLoading] = useState<boolean>(false);
    const [selectedHotInfoOption, setSelectedHotInfoOption] = useState<string[]>([]);

    const saveStopAPI = async (): Promise<void> => {
        const token = localStorage.getItem("token"); // Retrieve token (or other logic to get it)
        const trackId = trackData.trackId; // Get the track ID from the context

        if (!wptId || !trackId || !token || !language) {
            return;
        }

        await saveStop({
            token,
            trackId: trackId,
            waypointId: wptId,
            language,
            hotInfoIds: selectedHotInfoOption,
        });

        navigateToNext(wptId, language);
    };

    const getHotInfo = async (trackId: string): Promise<void> => {
        setLoading(true);
        const token = localStorage.getItem("token");

        if (!token) {
            return;
        }

        const response = await getHotInfoTrackId({ token, trackId });

        if (response) {
            const dataForDropDown: DropdownOption[] = response.map((item: { id: string; internalName: string }) => ({
                value: item.id,
                label: item.internalName,
            }));
            setHotInfos(dataForDropDown);
        }
        setLoading(false);
    };

    useEffect(() => {
        if (trackData && trackData.trackId) {
            getHotInfo(trackData.trackId);
        }
    }, [trackData.trackId]);

    const handleAudioOptionChange = (selectedOptions: string[]): void => {
        const updatedOptions = selectedOptions.filter((option) => option !== "ALL");
        setSelectedHotInfoOption(updatedOptions);
    };

    const getWaypoint = async (id: string): Promise<void> => {
        const token = localStorage.getItem("token");
        if (!token || !language) {
            return;
        }
        const waypointData = await getStopById({ token, id, language });

        if (waypointData && waypointData.waypoint) {
            const hotinfoData = waypointData.waypoint.infoChaude;
            const selectedHotInfoOpt: string[] = [];
            for (let i = 0; i < hotinfoData.length; i++) {
                const hotinfo = hotinfoData[i];
                selectedHotInfoOpt.push(hotinfo.id);
            }
            setSelectedHotInfoOption(selectedHotInfoOpt);
        }
    };

    useEffect(() => {
        if (wptId && wptId !== "") {
            getWaypoint(wptId);
        }
    }, [wptId, language]);

    return (
        <div className={`experience-stop-edit ${stopEdit.root}`}>
            <TitleSubtitle title={t("stopActivitiesTitle")} subtitle={t("stopActivitiesSubtitle")} layout="step" />
            <div className={`experience-stop-field ${stopEdit.field}`}>
                <h4 className={`experience-stop-field-label ${stopEdit.fieldLabel}`}>{t("addAnActivity")}</h4>
                <MultiSelectDropdown
                    label=""
                    options={hotInfos}
                    onChange={handleAudioOptionChange}
                    value={selectedHotInfoOption}
                    loading={loading}
                    placeholder={t("selectAnOption")}
                    error={false}
                    disabled={false}
                />
            </div>
            <div className={`${stepStyles.stepButton} ${stepStyles.footer}`}>
                <Button
                    variant="primary"
                    shape="pill"
                    className={stepStyles.continueBtn}
                    label={t("continue")}
                    onClick={saveStopAPI}
                />
            </div>
        </div>
    );
};

export default StopActivities;
